/**
 * Admin — Manual UPI payment verification.
 * Displays customer-submitted UPI transactions awaiting verification.
 * On approve → all linked invoices flip to `paid` and a transaction row
 * is written to the finance ledger. On reject → invoices unlock (back
 * to `unpaid`) so the customer can retry.
 */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, ShieldCheck, Phone, Receipt } from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending_verification: "bg-[#F9E4B7] text-[#7A5A11]",
  verified: "bg-[#D4E0D1] text-[#1A3317]",
  rejected: "bg-[#F4C7B9] text-[#7A2E18]",
};

export default function AdminManualPayments() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending_verification");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const q = filter && filter !== "all" ? `?status=${filter}` : "";
    api.get(`/manual-payments${q}`)
       .then(r => {
         // Iter 135 — defensive: accept both `[]` and `{items: []}`. The
         // list previously appeared blank in production because a 401
         // fell into the `.then` branch with an empty body; log noisily
         // so future issues are visible in DevTools.
         const rows = Array.isArray(r.data) ? r.data
                    : Array.isArray(r.data?.items) ? r.data.items
                    : [];
         if (!Array.isArray(r.data)) {
           // eslint-disable-next-line no-console
           console.warn("[manual-payments] non-array response:", r.data);
         }
         setItems(rows);
       })
       .catch(e => {
         // eslint-disable-next-line no-console
         console.error("[manual-payments] load failed:", e);
         toast.error(formatApiError(e));
         setItems([]);
       })
       .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (m) => {
    const note = window.prompt(
      `Approve UPI payment of ₹${m.amount.toFixed(2)}?\n\n` +
      `Txn ID: ${m.upi_txn_id}\n` +
      `Customer: ${m.customer_name}\n\n` +
      "Optional admin note:", "Verified against bank UPI log"
    );
    if (note === null) return;
    try {
      await api.post(`/manual-payments/${m.id}/approve`, { admin_note: note || null });
      toast.success(`Approved · ${m.invoice_ids.length} invoice(s) marked paid`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reject = async (m) => {
    const note = window.prompt(
      `Reject this UPI submission?\n\nTxn ID: ${m.upi_txn_id}\n\n` +
      "Please provide a reason (shown to admins only):", ""
    );
    if (note === null || !note.trim()) return;
    try {
      await api.post(`/manual-payments/${m.id}/reject`, { admin_note: note });
      toast.success("Rejected · invoices unlocked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Manual UPI Verifications"
              subtitle="Approve or reject customer-submitted UPI transactions">
      <div className="flex items-center gap-2 mb-4" data-testid="manual-filter-tabs">
        {[
          { k: "pending_verification", l: "Pending", tone: "#7A5A11" },
          { k: "verified", l: "Verified", tone: "#1A3317" },
          { k: "rejected", l: "Rejected", tone: "#7A2E18" },
          { k: "all", l: "All" },
        ].map(t => (
          <button key={t.k}
                  onClick={() => setFilter(t.k)}
                  data-testid={`manual-tab-${t.k}`}
                  className={`h-9 px-4 rounded-md text-xs uppercase tracking-[0.18em] ${
                    filter === t.k ? "bg-[#2D5A27] text-white" : "bg-white border border-[#E5E2D9] text-[#5C5A55]"
                  }`}>{t.l}</button>
        ))}
        <span className="ml-auto text-[11px] text-[#5C5A55]" data-testid="manual-count">
          {loading ? "Loading…" : `${items.length} row${items.length !== 1 ? "s" : ""}`}
        </span>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Submitted</th>
              <th className="px-6 py-3">Customer</th>
              <th className="px-6 py-3">Amount</th>
              <th className="px-6 py-3">UPI txn ID</th>
              <th className="px-6 py-3">App</th>
              <th className="px-6 py-3">Invoices</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-6 py-12 text-center text-[#8A8780]">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-16 text-center text-[#8A8780]">
                <ShieldCheck size={30} weight="duotone" color="#8A8780" className="mx-auto mb-2"/>
                No submissions in this bucket.
              </td></tr>
            )}
            {items.map((m, i) => (
              <tr key={m.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} data-testid={`manual-row-${m.id}`}>
                <td className="px-6 py-3 text-xs">
                  {m.submitted_at ? new Date(m.submitted_at).toLocaleString() : "—"}
                </td>
                <td className="px-6 py-3">
                  <div className="font-medium">{m.customer_name}</div>
                  <div className="text-[11px] text-[#8A8780] flex items-center gap-1"><Phone size={10}/>{m.customer_phone}</div>
                  {m.payer_upi_id && <div className="text-[10px] font-mono text-[#5C5A55] mt-0.5">{m.payer_upi_id}</div>}
                </td>
                <td className="px-6 py-3 font-heading text-lg">₹{Number(m.amount).toFixed(2)}</td>
                <td className="px-6 py-3 font-mono text-xs">{m.upi_txn_id}</td>
                <td className="px-6 py-3 text-xs capitalize">{m.upi_app || "—"}</td>
                <td className="px-6 py-3 text-xs">
                  <div className="flex items-center gap-1">
                    <Receipt size={11}/>{(m.invoice_nos || []).length} invoice(s)
                  </div>
                  <div className="text-[10px] text-[#8A8780] mt-0.5 truncate max-w-[200px]">
                    {(m.invoice_nos || []).join(", ")}
                  </div>
                </td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATUS_STYLE[m.status] || ""}`}>
                    {m.status.replace("_", " ")}
                  </span>
                  {m.admin_note && <div className="text-[10px] text-[#8A8780] mt-1 max-w-[220px]">{m.admin_note}</div>}
                </td>
                <td className="px-6 py-3 text-right">
                  {m.status === "pending_verification" ? (
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`manual-approve-${m.id}`}
                              onClick={() => approve(m)}
                              className="text-[#2D5A27] text-xs inline-flex items-center gap-1">
                        <CheckCircle size={12} weight="fill"/>Approve
                      </button>
                      <button data-testid={`manual-reject-${m.id}`}
                              onClick={() => reject(m)}
                              className="text-[#C25D38] text-xs inline-flex items-center gap-1">
                        <XCircle size={12} weight="fill"/>Reject
                      </button>
                    </div>
                  ) : (
                    <span className="text-[10px] text-[#8A8780]">
                      {m.verified_at ? new Date(m.verified_at).toLocaleDateString() : ""}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 rounded-md bg-[#F7F6F2] border border-[#E5E2D9] p-4 text-xs text-[#5C5A55]">
        <div className="font-medium text-[#1A1A1A] mb-1">How this works</div>
        Customer pays via any UPI app to our merchant VPA, then submits the
        transaction ID here. Approving locks in the payment, marks all
        listed invoices Paid, and writes a `manual_upi` row to the
        transactions ledger. Rejecting unlocks the invoices so the customer
        can retry (or use Razorpay instead).
      </div>
    </AppShell>
  );
}
